import React from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { ClientProvider } from './contexts/ClientContext';
import { DebugProvider } from './contexts/DebugContext';
import { ExtendedModeProvider } from './contexts/ExtendedModeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import Footer from './components/Footer';
import VideoGreetingWrapper from './components/VideoGreetingWrapper';
import DebugDrawer from './components/debug/DebugDrawer';
import { useFirebaseInit } from './hooks/useFirebaseInit';
import { useEnvBackground } from './hooks/useEnvBackground';
import './i18n';

// Wrapper que decide si activar el debug drawer (solo para superadmin).
// Cero overhead para usuarios comunes: el provider se monta en modo
// `enabled=false` y el drawer no se renderiza.
function DebugLayer({ children }: { children: React.ReactNode }) {
  const { userData } = useAuth();
  const isSuperadmin = userData?.user_type === 'superadmin';
  return (
    <DebugProvider enabled={isSuperadmin}>
      <ExtendedModeProvider enabled={isSuperadmin}>
        {children}
        {isSuperadmin && <DebugDrawer />}
      </ExtendedModeProvider>
    </DebugProvider>
  );
}

// Shell raíz: layout full-height. El tinte de fondo por ambiente
// (localhost / staging / producción) se aplica ACÁ, en el div `id="env-bg-root"`
// (min-h-screen), que es el único contenedor que envuelve TODAS las vistas
// (tabla, admin, config, selector de cliente). Las vistas hijas ya no pintan un
// `bg-gray-50` opaco: son transparentes para que este tinte se vea en toda la app.
function AppShell() {
  const { bgClass: envBgClass } = useEnvBackground();
  return (
    <VideoGreetingWrapper>
      <div id="env-bg-root" className={`min-h-screen flex flex-col ${envBgClass}`}>
        <div className="flex-1">
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        </div>
        <Footer />
      </div>
    </VideoGreetingWrapper>
  );
}

function AppContent() {
  return (
    <AuthProvider>
      <ClientProvider>
        <AdminProvider>
          <DebugLayer>
            <AppShell />
          </DebugLayer>
        </AdminProvider>
      </ClientProvider>
    </AuthProvider>
  );
}

function App() {
  const { initialized, error } = useFirebaseInit();

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md mx-4">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error de Inicialización</h2>
          <p className="text-gray-600 mb-4">Hubo un problema al conectar con Firebase:</p>
          <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Inicializando aplicación...</p>
        </div>
      </div>
    );
  }

  return <AppContent />;
}

export default App;
